/**
 * Transactions — the full general ledger as individual LINES (one row per debit
 * or credit), most-recent first. Journals shows one row per entry; this shows
 * every posting "one by one", filterable by date range + account. Reuses the
 * server-side /general-ledger/transactions endpoint (mirrors accountHistory).
 */
import type { Account, AccountLedgerRow } from '@erp/shared';
import { ArrowDownUp, TrendingDown, TrendingUp } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Card, Skeleton } from '../../components/shadcn/index.js';
import {
  Button,
  Combobox,
  type ComboboxOption,
  DataTable,
  type DataTableColumn,
  EmptyState,
  Input,
  StatCard,
} from '../../components/ui/index.js';
import { api } from '../../lib/api.js';
import { useAuthStore } from '../../lib/authStore.js';
import { appUrl } from '../../lib/basePath.js';
import { formatDate, formatNumber } from '../../lib/intl.js';
import { formatMoney } from '../../lib/money.js';

/** One request's worth of lines — the endpoint's own maximum. */
const PAGE_LIMIT = 1000;

/** Totals over the whole filter (server-side), in base and per transaction currency. */
interface TxTotals {
  baseDebit: string;
  baseCredit: string;
  byCurrency: Array<{ currencyCode: string; debit: string; credit: string }>;
}

export function TransactionsPage(): JSX.Element {
  const { t, i18n } = useTranslation('gl');
  const { t: tc } = useTranslation();
  const locale = i18n.language;

  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [accountId, setAccountId] = useState<string | undefined>(undefined);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [rows, setRows] = useState<AccountLedgerRow[] | null>(null);
  const [total, setTotal] = useState(0);
  const [sums, setSums] = useState<TxTotals | null>(null);

  useEffect(() => {
    void api().get<{ items: Account[] }>('/accounts').then((r) => setAccounts(r.data.items)).catch(() => undefined);
  }, []);

  /**
   * EVERY matching line, not the first 500. The page used to stop at one page
   * (Duhok has 10,000+ lines) and asked for narrower dates; it now pages through
   * the server's maximum of 1,000 at a time, filling in as pages arrive. The
   * totals above always come from the server, over the whole filter.
   */
  useEffect(() => {
    let alive = true;
    setRows(null);
    void (async () => {
      const all: AccountLedgerRow[] = [];
      try {
        for (let offset = 0; ; offset += PAGE_LIMIT) {
          const params = new URLSearchParams({ limit: String(PAGE_LIMIT), offset: String(offset) });
          if (from) params.set('from', from);
          if (to) params.set('to', to);
          if (accountId) params.set('accountId', accountId);
          const r = await api()
            .get<{ items: AccountLedgerRow[]; total: number; totals?: TxTotals }>(`/general-ledger/transactions?${params.toString()}`);
          if (!alive) return;
          all.push(...r.data.items);
          setRows([...all]);
          setTotal(r.data.total);
          if (offset === 0) setSums(r.data.totals ?? null);
          if (r.data.items.length < PAGE_LIMIT || all.length >= r.data.total) return;
        }
      } catch {
        if (alive && all.length === 0) { setRows([]); setTotal(0); }
      }
    })();
    return () => { alive = false; };
  }, [from, to, accountId]);

  const accountOptions: ComboboxOption[] = useMemo(
    () =>
      accounts
        .filter((a) => a.isActive)
        .map((a) => ({ value: a.id, label: `${a.code} — ${a.name}`, keywords: `${a.code} ${a.name}` })),
    [accounts],
  );

  // The tenant's base, not the first row's — an empty filter used to read "$0.00".
  const baseCurrency = useAuthStore((st) => st.tenant?.baseCurrencyCode) ?? rows?.[0]?.baseCurrencyCode ?? '';
  // Over every matching line (server totals), not only the page loaded.
  const totals = useMemo(() => ({
    d: Number(sums?.baseDebit ?? (rows ?? []).reduce((a, l) => a + Number(l.baseDebit), 0)),
    c: Number(sums?.baseCredit ?? (rows ?? []).reduce((a, l) => a + Number(l.baseCredit), 0)),
  }), [rows, sums]);
  const perCcy = (sums?.byCurrency ?? []).filter((x) => x.currencyCode !== baseCurrency);
  const m = (n: number, cur: string): string => formatMoney(n.toFixed(4), { locale, currency: cur });

  const hasFilters = Boolean(from || to || accountId);
  const clearFilters = (): void => { setFrom(''); setTo(''); setAccountId(undefined); };

  const header = (
    <header className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <h1 className="text-2xl font-semibold">{t('transactions.title')}</h1>
        <p className="mt-1 text-sm text-fg-muted">{t('transactions.description')}</p>
      </div>
    </header>
  );

  const filters = (
    <Card className="flex flex-wrap items-end gap-3 p-4">
      <label className="flex flex-col gap-1 text-sm">
        <span className="text-fg-muted">{t('transactions.filters.from')}</span>
        <Input type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} />
      </label>
      <label className="flex flex-col gap-1 text-sm">
        <span className="text-fg-muted">{t('transactions.filters.to')}</span>
        <Input type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} />
      </label>
      <label className="flex min-w-[16rem] flex-col gap-1 text-sm">
        <span className="text-fg-muted">{t('transactions.filters.account')}</span>
        <Combobox
          options={accountOptions}
          value={accountId}
          onChange={(v) => setAccountId(v)}
          placeholder={t('transactions.filters.allAccounts')}
        />
      </label>
      {hasFilters && (
        <Button variant="secondary" onClick={clearFilters}>
          {t('transactions.filters.clear')}
        </Button>
      )}
    </Card>
  );

  const columns: DataTableColumn<AccountLedgerRow>[] = [
    {
      id: 'entryDate',
      header: t('transactions.columns.entryDate'),
      sortValue: (l) => l.entryDate,
      cell: (l) => formatDate(l.entryDate, { locale }),
    },
    {
      id: 'entryNumber',
      header: t('transactions.columns.entryNumber'),
      sortValue: (l) => l.entryNumber,
      searchValue: (l) => l.entryNumber,
      cellClassName: 'font-mono text-xs',
      cell: (l) => (
        <button
          type="button"
          className="text-brand hover:underline"
          onClick={() => window.open(appUrl(`/accounting/journals/${l.entryId}/print`), '_blank')}
          title={tc('actions.print', { defaultValue: 'Print' })}
        >
          {l.entryNumber}
        </button>
      ),
    },
    {
      id: 'account',
      header: t('transactions.columns.account'),
      sortValue: (l) => l.accountCode,
      searchValue: (l) => `${l.accountCode} ${l.accountName}`,
      cellClassName: 'font-medium',
      cell: (l) => (
        <Link to={`/accounting/accounts/${l.accountId}`} className="hover:underline">
          <span className="font-mono text-xs text-fg-muted">{l.accountCode}</span>{' '}
          <span>{l.accountName}</span>
        </Link>
      ),
    },
    {
      id: 'description',
      header: t('transactions.columns.description'),
      sortValue: (l) => l.description ?? '',
      searchValue: (l) => l.description ?? '',
      cell: (l) => l.description ?? <span className="text-fg-muted">—</span>,
    },
    {
      id: 'debit',
      header: t('transactions.columns.debit'),
      align: 'end',
      sortValue: (l) => Number(l.debit),
      cellClassName: 'font-mono tabular-nums',
      cell: (l) => (Number(l.debit) ? m(Number(l.debit), l.transactionCurrencyCode) : <span className="text-fg-muted">—</span>),
    },
    {
      id: 'credit',
      header: t('transactions.columns.credit'),
      align: 'end',
      sortValue: (l) => Number(l.credit),
      cellClassName: 'font-mono tabular-nums',
      cell: (l) => (Number(l.credit) ? m(Number(l.credit), l.transactionCurrencyCode) : <span className="text-fg-muted">—</span>),
    },
    {
      id: 'currency',
      header: t('transactions.columns.currency'),
      align: 'end',
      sortValue: (l) => l.transactionCurrencyCode,
      cellClassName: 'font-mono text-xs text-fg-muted',
      cell: (l) => l.transactionCurrencyCode,
    },
  ];

  if (!rows) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        {filters}
        <Card className="p-6"><Skeleton className="h-64 w-full" /></Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {header}
      {filters}
      <div className="grid gap-3 sm:grid-cols-3">
        <StatCard
          label={t('transactions.totalDebit', { defaultValue: 'Total debits' })}
          value={m(totals.d, baseCurrency)}
          icon={TrendingUp}
          tone="success"
          hint={perCcy.length ? perCcy.map((x) => `${t('transactions.ofWhich')} ${m(Number(x.debit), x.currencyCode)}`).join(' · ') : undefined}
        />
        <StatCard
          label={t('transactions.totalCredit', { defaultValue: 'Total credits' })}
          value={m(totals.c, baseCurrency)}
          icon={TrendingDown}
          tone="info"
          hint={perCcy.length ? perCcy.map((x) => `${t('transactions.ofWhich')} ${m(Number(x.credit), x.currencyCode)}`).join(' · ') : undefined}
        />
        <StatCard label={t('transactions.lines', { defaultValue: 'Lines' })} value={formatNumber(total, { locale })} icon={ArrowDownUp} />
      </div>
      {rows.length === 0 ? (
        <EmptyState
          icon={<ArrowDownUp className="h-8 w-8" aria-hidden />}
          title={t('transactions.empty')}
          description={t('transactions.emptyDescription')}
        />
      ) : (
        <Card className="overflow-hidden p-0">
          <DataTable columns={columns} data={rows} rowKey={(l) => l.id} />
          {total > rows.length && (
            <p className="border-t border-border px-4 py-2.5 text-xs text-fg-muted">
              {t('transactions.loading', { have: formatNumber(rows.length, { locale }), total: formatNumber(total, { locale }), defaultValue: 'Loading {{have}} of {{total}} lines…' })}
            </p>
          )}
        </Card>
      )}
    </div>
  );
}
